<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto sm:px-6 lg:px-8 px-5">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.plus-circle-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Create custom repair invoice') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-red shadow-sm rounded-md" to="/admin/repair-orders">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <form @submit.prevent="submitRepair">
          <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
            <!-- customer information -->
            <template>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Customer Information') }}</h3>
                <p class="form-description-text">{{ $t('Valid customer information required to contact letter') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="name">{{ $t('Name') }}</label>
                      <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="repair.name" />
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="email">{{ $t('Email address') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Email address')"
                          class="form-input"
                          id="email"
                          required
                          type="email"
                          v-model="repair.email"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="phone">{{ $t('Phone Number') }}</label>
                      <div class="pt-1">
                        <input :placeholder="$t('Phone Number')" class="form-input" id="phone" v-model="repair.phone" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="serial_number">{{ $t('Serial number') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Serial number')"
                          class="form-input rounded-0"
                          id="serial_number"
                          v-model="repair.serial_number"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="address">{{ $t('Home address') }}</label>
                    <div class="pt-1">
                      <input :placeholder="$t('Home address')" class="form-input" id="address" v-model="repair.address" />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="diagnostics">{{ $t('Initial diagnostics') }}</label>
                    <div class="pt-1">
                      <input-wysiwyg id="diagnostics" v-model="repair.diagnostics"></input-wysiwyg>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- customer info ends -->
            <!-- brand list start -->
            <div class="md:col-span-3">
              <div class="py-3">
                <div class="border-t border-gray-200"></div>
              </div>
            </div>
            <div class="md:col-span-1">
              <h3 class="form-description-head">{{ $t('Enter device brand') }}</h3>
              <p class="form-description-text">{{ $t('Brands we repair') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-3">
                <div class="col-span-3">
                  <label class="form-label" for="brand_info">{{ $t('Brand name') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Brand name')" class="form-input rounded-0" id="brand_info" v-model="repair.brand_info.name" />
                  </div>
                </div>
              </div>
            </div>
            <!-- brand list end -->
            <!-- device list end -->
            <template v-if="repair.brand_info.name">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Enter device name') }}</h3>
                <p class="form-description-text">{{ $t('Fill up device name and model') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <label class="form-label" for="device_info_name">{{ $t('Device name') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Device name')"
                        class="form-input rounded-0"
                        id="device_info_name"
                        v-model="repair.device_info.name"
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="device_info_model">{{ $t('Device model') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Device model')"
                        class="form-input rounded-0"
                        id="device_info_model"
                        v-model="repair.device_info.model"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- device list end -->
            <!-- defects list start -->
            <template v-if="repair.device_info.name && repair.device_info.model">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Enter defect information') }}</h3>
                <p class="form-description-text">{{ $t('Write the issues detail that need to be fixed, total price and total cost') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <label class="form-label" for="defects_info_title">{{ $t('Issues') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Issues or problems')"
                        class="form-input rounded-0"
                        id="defects_info_title"
                        v-model="repair.defects_info.title"
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="defects_info_price">{{ $t('Price or charges') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Price or charges')"
                        class="form-input rounded-0"
                        id="defects_info_price"
                        type="number"
                        v-model="repair.defects_info.price"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="defects_info_cost">{{ $t('Cost') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Cost')"
                        class="form-input rounded-0"
                        type="number"
                        id="defects_info_cost"
                        v-model="repair.defects_info.cost"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- defects list end -->
            <!-- priority list start -->
            <template v-if="priorityList.length && repair.defects_info.title && repair.defects_info.price">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Select priority') }}</h3>
                <p class="form-description-text">{{ $t('Repair processing priority') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(priority, index) in priorityList">
                        <div
                          :key="index"
                          @click="selectPriority(priority)"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.repair_priority_id === priority.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ priority.name }}
                            </p>
                          </div>
                          <div class="ml-auto">
                            <small>{{ priority.extra_charge | currency }}</small>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- priority list end -->
            <!-- technician list start -->
            <template v-if="technicians.length && repair.defects_info.title && repair.defects_info.price">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Assign and payment') }}</h3>
                <p class="form-description-text">
                  {{ $t('Assign repair to technician,enter prepaid or advance amount.') }}
                </p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div class="bg-white text-right px-4 py-3 sm:px-6">
                      <strong>{{ $t('Subtotal') }}</strong>
                      : {{ subTotalAmount | currency }}
                      <br />
                      <strong>{{ $t('Priority Process Charges') }}</strong>
                      : {{ currentPriority.extra_charge | currency }}
                      <br />
                      <strong>{{ $t('Tax') }}</strong>
                      : {{ taxAmount | currency }}
                      <br />
                      <strong>{{ $t('Total Amount') }}</strong>
                      : {{ totalAmountWithTax | currency }}
                    </div>
                  </div>
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(technician, index) in technicians">
                        <div
                          :key="index"
                          @click="repair.user_id = technician.id"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.user_id === technician.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ technician.name }}({{ technician.email }})
                            </p>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="pre_paid">{{ $t('Advance or prepaid amount') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Advance or prepaid amount')"
                        class="form-input"
                        id="pre_paid"
                        required
                        type="number"
                        v-model="repair.pre_paid"
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="is_device_collected">{{ $t('Device collected status') }}</label>
                    <input-switch
                      :disabled-label="$t('The customer will be notified to dispatch device')"
                      :enabled-label="$t('The device has been just collected from customer')"
                      id="is_device_collected"
                      v-model="repair.is_device_collected"
                    ></input-switch>
                    <div class="form-description-text">
                      {{ $t('This option will control to notify customer to dispatch his device via email') }}.
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="has_warranty">{{ $t('Device warranty status') }}</label>
                    <input-switch
                      :disabled-label="$t('No')"
                      :enabled-label="$t('Yes')"
                      id="has_warranty"
                      v-model="repair.has_warranty"
                    ></input-switch>
                    <div class="form-description-text">{{ $t('This option will set device has warranty or not') }}.</div>
                  </div>
                </div>
              </div>
            </template>
            <!-- priority list end -->
          </div>
          <div v-if="repair.defects_info.title && repair.defects_info.price" class="card-footer">
            <span class="inline-flex">
              <router-link class="btn btn-secondary shadow-sm rounded-md mr-4" to="/admin/repair-orders">{{ $t('Cancel') }}</router-link>
              <button class="btn btn-app shadow-sm rounded-md" type="submit">
                {{ $t('Submit') }}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'new',
  data() {
    return {
      loading: false,
      technicians: [],
      priorityList: [],
      currentPriority: {
        extra_charge: 0,
      },
      tax: {
        is_vat: false,
        tax_rate: 0,
        is_tax_included: false,
        is_tax_fix: false,
      },
      repair: {
        is_device_collected: false,
        has_warranty: false,
        is_manual: true,
        name: null,
        email: null,
        phone: null,
        address: null,
        serial_number: null,
        diagnostics: null,
        brand_info: {
          name: null,
        },
        device_info: {
          avatar: '/images/default/icon.png',
          name: null,
          model: null,
          required_time: null,
        },
        defects_info: {
          title: null,
          price: 0,
          cost: 0,
        },
        repair_priority_id: 1,
        sub_total: 0,
        total_cost: 0,
        profit: 0,
        tax: 0,
        total_charges: 0,
        user_id: null,
        pre_paid: 0,
      },
    };
  },
  mounted() {
    this.getBrandAndPriorityList();
    this.getUsers();
  },
  computed: {
    repairCostAmount() {
      return this.repair.defects_info.cost;
    },
    subTotalAmount() {
      return this.repair.defects_info.price;
    },
    profit() {
      return Number(this.totalAmountWithTax - this.repairCostAmount - this.taxAmount);
    },
    subTotalWithPriority() {
      const amount = Number(this.subTotalAmount) + Number(this.currentPriority.extra_charge);
      return amount;
    },
    taxAmount() {
      const amount = this.subTotalWithPriority;
      const rate = Number(this.tax.tax_rate ?? this.tax.rate);
      if (this.tax.is_vat) {
        if (this.tax.is_tax_included) {
          const vatAmount = amount - amount / (1 + rate / 100);
          return vatAmount.toFixed(2);
        } else {
          const vatAmount = amount * (rate / 100);
          return vatAmount.toFixed(2);
        }
      } else {
        if (this.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
    totalAmountWithTax() {
      if (this.tax.is_tax_included) {
        return this.subTotalWithPriority;
      }
      return this.subTotalWithPriority + Number(this.taxAmount);
    },
  },
  methods: {
    getUsers() {
      this.loading = true;
      axios
        .get(route('users.technicians'))
        .then((response) => {
          this.technicians = response.data.technicians;
          this.loading = false;
        })
        .catch(() => {
          this.loading = true;
        });
    },
    selectPriority(priority) {
      this.currentPriority = priority;
      this.repair.repair_priority_id = priority.id;
    },
    submitRepair() {
      this.loading = true;
      this.repair.sub_total = this.subTotalAmount;
      this.repair.total_charges = this.totalAmountWithTax;
      this.repair.profit = this.profit;
      this.repair.total_cost = this.repairCostAmount;
      this.repair.defects = this.currentDefects;
      this.repair.tax = this.taxAmount;
      this.repair.payment_gateway_key = 'biller';
      this.repair.payment_gateway_value = this.$store.state.user.name;
      axios
        .post(route('repair-orders.store'), this.repair)
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading = false;
          this.$router.push(`/admin/repair-orders/${response.data.order.uuid}/handle`);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getBrandAndPriorityList() {
      this.loading = true;
      axios
        .get(route('repair.brand.list'))
        .then((response) => {
          this.getTax();
          this.priorityList = response.data.priorities;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getTax() {
      axios
        .get(route('settings.get.tax'))
        .then((response) => {
          this.tax = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
